import { AppSidebar } from "@/components/nav/app-sidebar";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useProject } from "@/hooks/api/use-projects";
import React from "react";
import { Link, useLocation } from "react-router";

interface NavItem {
  title: string;
  href: string;
  icon?: React.ReactNode;
}

interface AppLayoutProps {
  children: React.ReactNode;
  navItems?: NavItem[];
}

export default function AppLayout({ children, navItems = [] }: AppLayoutProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const projectMatch = currentPath.match(/\/projects\/(\d+)/);
  const projectId = projectMatch ? projectMatch[1] : null;
  const competitorMatch = currentPath.match(/\/competitors\/(\d+)/);
  const competitorId = competitorMatch ? competitorMatch[1] : null;
  const { data: project } = useProject(projectId || "");

  // Build breadcrumb items based on current path
  const getBreadcrumbs = () => {
    const paths = currentPath.split("/").filter(Boolean);
    if (paths.length === 0) return [];

    return paths.reduce<Array<{ title: string; href: string }>>(
      (acc, path, index) => {
        let href = `/${paths.slice(0, index + 1).join("/")}`;
        const navItem = navItems.find((item) => item.href === href);
        let title = navItem?.title;

        if (!title) {
          if (path === "projects") {
            title = "Projects";
          } else if (path === "competitors") {
            title = "Competitors";
            href = `/projects/${projectId}`;
          } else if (path === projectId && paths[index - 1] === "projects") {
            title = project?.name || "Project";
          } else if (
            path === competitorId &&
            paths[index - 1] === "competitors"
          ) {
            const competitor = project?.competitors?.find(
              (c) => c.id === Number(competitorId)
            );
            title = competitor?.name || "Competitor";
          } else {
            title = path.charAt(0).toUpperCase() + path.slice(1);
          }
        }

        acc.push({ title, href });
        return acc;
      },
      []
    );
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={`${crumb.href}-${index}`}>
                    <BreadcrumbItem>
                      {index === breadcrumbs.length - 1 ? (
                        <BreadcrumbPage>{crumb.title}</BreadcrumbPage>
                      ) : (
                        <Link to={crumb.href} className="breadcrumb-link">
                          {crumb.title}
                        </Link>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="container max-w-7xl flex flex-1 flex-col gap-4 p-4 pt-0">
          {children}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
